import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  uploadImageFile,
  saveProfileImageToDatabase 
} from '@/services/profileImage';

// Interface for the hook's return value
interface UseProfileImageUploaderReturn {
  isUploading: Map<string, boolean>;
  uploadErrors: Map<string, string>;
  uploadImageForProfile: (profileId: string, imageFile: File) => Promise<string>;
}

export const useProfileImageUploader = (): UseProfileImageUploaderReturn => {
  const [isUploading, setIsUploading] = useState<Map<string, boolean>>(new Map());
  const [uploadErrors, setUploadErrors] = useState<Map<string, string>>(new Map());
  const { toast } = useToast();

  const uploadImageForProfile = useCallback(async (profileId: string, imageFile: File): Promise<string> => {
    // Set uploading state
    setIsUploading(current => new Map(current).set(profileId, true));
    setUploadErrors(current => {
      const updated = new Map(current);
      updated.delete(profileId);
      return updated;
    });

    try {
      console.log(`📤 Téléchargement d'une image personnalisée pour le profil ${profileId}`);
      toast({ title: 'Téléchargement en cours', description: `Téléchargement de l'image pour le profil ${profileId}...` });

      // Upload to Supabase storage
      const uploadResult = await uploadImageFile(profileId, imageFile);

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Upload failed');
      }

      const imageUrl = uploadResult.url!;

      // Save URL to database (assuming uploaded image should be the main one)
      await saveProfileImageToDatabase(profileId, imageUrl, true);
      console.log(`✅ Image téléversée et enregistrée dans la base de données pour ${profileId}`);

      toast({
        title: 'Téléchargement réussi',
        description: `Image téléversée avec succès pour le profil ${profileId}`,
      });

      return imageUrl; // Return the URL of the uploaded image

    } catch (error) {
      console.error(`❌ Erreur lors du téléchargement de l'image pour le profil ${profileId}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      setUploadErrors(current => new Map(current).set(profileId, errorMessage));
      toast({
        title: 'Échec du téléchargement',
        description: `Impossible de télécharger l'image : ${errorMessage}`,
        variant: 'destructive',
      });
      throw error; // Re-throw error for the caller to handle
    } finally {
      setIsUploading(current => new Map(current).set(profileId, false));
    }
  }, [toast]);

  return { isUploading, uploadErrors, uploadImageForProfile };
}; 